import { RTableContent } from "types/engineOutput.types"

export interface StatGrid {
  /** One entry per data column: the banner path joined with "//", and the statistic. */
  columns: { key: string; stat: string }[]
  rows: { label: string | null; values: (string | number | null)[] }[]
  /** Banner path to the letter this output gave it. Empty without significance. */
  letters: Record<string, string>
}

const STATISTIC = "Statistic"

/** Reads a transposed DTAB output. An output that is not one gives an empty grid. */
export const readStatGrid = (content: RTableContent): StatGrid => {
  const data = Array.isArray(content?.data) ? content.data : []
  const at = data.findIndex((row) => Array.isArray(row) && row[0] === STATISTIC)
  if (at < 0) return { columns: [], rows: [], letters: {} }
  const first = 1
  const statRow = data[at]

  const levels = data.slice(0, at).map((row) => {
    const filled: (string | null)[] = []
    let current: string | null = null
    for (let c = first; c < statRow.length; c++) {
      const cell = row[c]
      if (typeof cell === "string" && cell !== "") current = cell
      filled.push(current)
    }
    return filled
  })
  const columns = statRow.slice(first).map((stat, i) => ({
    key: levels
      .map((level) => level[i])
      .filter((part): part is string => part !== null)
      .join("//"),
    stat: String(stat ?? ""),
  }))

  const rows = data.slice(at + 1).map((row) => ({
    label: typeof row[0] === "string" && row[0] !== "" ? row[0] : null,
    values: row.slice(first) as (string | number | null)[],
  }))

  const letters: Record<string, string> = {}
  const elements = (content.significance as { banner?: { elements?: unknown[] } } | undefined)
    ?.banner?.elements
  ;(Array.isArray(elements) ? elements : []).forEach((e) => {
    const el = e as { header?: unknown; letter?: unknown }
    if (Array.isArray(el.header) && typeof el.letter === "string")
      letters[el.header.join("//")] = el.letter
  })
  return { columns, rows, letters }
}

/** The value of `stat` for the banner column `key` in the row `label` (null = the first row). */
export const statValue = (
  grid: StatGrid,
  label: string | null,
  key: string,
  stat: string
): string | number | null => {
  const row = label === null ? grid.rows[0] : grid.rows.find((r) => r.label === label)
  const c = grid.columns.findIndex((col) => col.key === key && col.stat === stat)
  return row && c >= 0 ? (row.values[c] ?? null) : null
}
